<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FlowSelect from '../config/FlowSelect.vue';
import FlowCard from './FlowCard.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const accountId = computed(() => Number(route.params.accountId));
const searchQuery = ref('');
const statusFilter = ref('');

// flow create
const showCreateModal = ref(false);
const newFlowName = ref('');
const isCreating = ref(false);

// flow delete
const flowToDelete = ref(null);
const showDeleteModal = ref(false);

// folder create/edit
const showFolderModal = ref(false);
const folderModalMode = ref('create'); // 'create' | 'edit'
const folderModalName = ref('');
const editingFolder = ref(null);
const isSavingFolder = ref(false);

// folder delete
const folderToDelete = ref(null);
const showFolderDeleteModal = ref(false);

// drag state
const draggingFlowId = ref(null);
const dragOverFolderId = ref(null); // null = "no folder" zone, folderId = folder zone

// collapsed folders
const collapsedFolders = ref(new Set());

const flows = computed(() => store.getters['flows/getFlows']);
const folders = computed(() => store.getters['flowFolders/getFolders'] ?? []);
const favoriteIds = computed(
  () => store.getters['flowUserFavorites/getFavoriteFlowIds']
);
const uiFlags = computed(() => store.getters['flows/getUIFlags']);

const filteredFlows = computed(() => {
  let list = flows.value;
  if (statusFilter.value) {
    list = list.filter(f => f.status === statusFilter.value);
  }
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(f => f.name.toLowerCase().includes(q));
  }
  return [...list].sort(
    (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
  );
});

const NO_FOLDER = 'nofolder';

const flowsByFolder = computed(() => {
  const map = {};
  folders.value.forEach(f => {
    map[f.id] = [];
  });
  map[NO_FOLDER] = [];

  filteredFlows.value.forEach(flow => {
    const key = flow.flow_folder_id ?? NO_FOLDER;
    if (!map[key]) map[key] = [];
    map[key].push(flow);
  });
  return map;
});

const statusOptions = computed(() => [
  { value: '', label: t('FLOWS.STATUS.ALL') },
  { value: 'draft', label: t('FLOWS.STATUS.DRAFT') },
  { value: 'active', label: t('FLOWS.STATUS.ACTIVE') },
  { value: 'paused', label: t('FLOWS.STATUS.PAUSED') },
  { value: 'archived', label: t('FLOWS.STATUS.ARCHIVED') },
]);

onMounted(() => {
  store.dispatch('flows/fetch');
  store.dispatch('flowFolders/fetch');
  store.dispatch('flowUserFavorites/fetch');
});

// ── Flow actions ──────────────────────────────────────────
async function createFlow() {
  if (!newFlowName.value.trim()) return;
  isCreating.value = true;
  try {
    const flow = await store.dispatch('flows/create', {
      name: newFlowName.value.trim(),
    });
    showCreateModal.value = false;
    newFlowName.value = '';
    router.push({
      name: 'flows_editor',
      params: { accountId: accountId.value, flowId: flow.id },
    });
  } catch {
    // handled by store
  } finally {
    isCreating.value = false;
  }
}

async function toggleFavorite(flowId) {
  if (favoriteIds.value.includes(flowId)) {
    await store.dispatch('flowUserFavorites/remove', flowId);
  } else {
    await store.dispatch('flowUserFavorites/add', flowId);
  }
}

function confirmDelete(flow) {
  flowToDelete.value = flow;
  showDeleteModal.value = true;
}

async function deleteFlow() {
  if (!flowToDelete.value) return;
  try {
    await store.dispatch('flows/destroy', flowToDelete.value.id);
  } finally {
    showDeleteModal.value = false;
    flowToDelete.value = null;
  }
}

// ── Folder actions ────────────────────────────────────────
function openCreateFolder() {
  folderModalMode.value = 'create';
  folderModalName.value = '';
  editingFolder.value = null;
  showFolderModal.value = true;
}

function openEditFolder(folder) {
  folderModalMode.value = 'edit';
  folderModalName.value = folder.name;
  editingFolder.value = folder;
  showFolderModal.value = true;
}

async function saveFolder() {
  if (!folderModalName.value.trim()) return;
  isSavingFolder.value = true;
  try {
    if (folderModalMode.value === 'create') {
      await store.dispatch('flowFolders/create', {
        name: folderModalName.value.trim(),
      });
    } else {
      await store.dispatch('flowFolders/update', {
        id: editingFolder.value.id,
        name: folderModalName.value.trim(),
      });
    }
    showFolderModal.value = false;
  } catch {
    // handled by store
  } finally {
    isSavingFolder.value = false;
  }
}

function confirmDeleteFolder(folder) {
  folderToDelete.value = folder;
  showFolderDeleteModal.value = true;
}

async function deleteFolder() {
  if (!folderToDelete.value) return;
  try {
    await store.dispatch('flowFolders/destroy', folderToDelete.value.id);
  } finally {
    showFolderDeleteModal.value = false;
    folderToDelete.value = null;
  }
}

function toggleCollapse(folderId) {
  const s = new Set(collapsedFolders.value);
  if (s.has(folderId)) s.delete(folderId);
  else s.add(folderId);
  collapsedFolders.value = s;
}

// ── Drag and drop ─────────────────────────────────────────
function onDragStart(flowId) {
  draggingFlowId.value = flowId;
}

function onDragEnd() {
  draggingFlowId.value = null;
  dragOverFolderId.value = null;
}

function onDragOver(e, zoneKey) {
  e.preventDefault();
  dragOverFolderId.value = zoneKey;
}

function onDragLeave() {
  dragOverFolderId.value = null;
}

async function onDrop(e, zoneKey) {
  e.preventDefault();
  dragOverFolderId.value = null;
  if (!draggingFlowId.value) return;

  const folderId = zoneKey === NO_FOLDER ? null : zoneKey;
  const flow = flows.value.find(f => f.id === draggingFlowId.value);
  if (!flow) return;
  if ((flow.flow_folder_id ?? null) === folderId) return;

  await store.dispatch('flows/update', {
    id: draggingFlowId.value,
    flow_folder_id: folderId,
  });

  draggingFlowId.value = null;
}
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('FLOWS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('FLOWS.TITLE')"
        :description="$t('FLOWS.DESCRIPTION')"
        :search-placeholder="$t('FLOWS.SEARCH_PLACEHOLDER')"
      >
        <template #actions>
          <span
            v-if="flows.length"
            class="text-sm text-n-slate-11 flex-shrink-0"
          >
            {{ filteredFlows.length }}
          </span>
          <div class="w-24 sm:w-40 min-w-0 flex-shrink">
            <FlowSelect v-model="statusFilter" :options="statusOptions" />
          </div>
          <button
            type="button"
            :title="$t('FLOWS.FOLDER.NEW')"
            class="rounded-lg border border-n-weak p-1.5 text-n-slate-11 hover:bg-n-alpha-1 transition flex-shrink-0"
            @click="openCreateFolder"
          >
            <span class="i-lucide-folder-plus size-4" />
          </button>
          <Button
            :label="$t('FLOWS.NEW_FLOW')"
            size="sm"
            class="flex-shrink-0"
            @click="showCreateModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <!-- Empty state — sem flows -->
      <div
        v-if="!uiFlags.isFetching && flows.length === 0"
        class="flex flex-col items-center justify-center h-64"
      >
        <p class="text-sm text-n-slate-10 text-center">
          {{ $t('FLOWS.EMPTY_STATE') }}
        </p>
      </div>

      <!-- Empty state após filtro -->
      <div
        v-else-if="filteredFlows.length === 0 && flows.length > 0"
        class="flex flex-col items-center justify-center h-64"
      >
        <p class="text-sm text-n-slate-10 text-center">
          {{ $t('FLOWS.EMPTY_STATE_FILTERED') }}
        </p>
      </div>

      <div v-else class="flex flex-col gap-4 mt-4">
        <!-- Folders -->
        <div
          v-for="folder in folders"
          :key="folder.id"
          class="flex flex-col rounded-xl border transition-colors"
          :class="
            dragOverFolderId === folder.id
              ? 'border-n-brand bg-n-alpha-2'
              : 'border-n-weak bg-n-background'
          "
          @dragover="onDragOver($event, folder.id)"
          @dragleave="onDragLeave"
          @drop="onDrop($event, folder.id)"
        >
          <!-- Folder header -->
          <div
            class="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none"
            @click="toggleCollapse(folder.id)"
          >
            <span
              class="text-n-slate-10 transition-transform"
              :class="
                collapsedFolders.has(folder.id)
                  ? 'i-lucide-chevron-right size-3.5'
                  : 'i-lucide-chevron-down size-3.5'
              "
            />
            <span class="i-lucide-folder size-4 text-n-slate-10" />
            <span class="text-sm font-medium text-n-slate-12 flex-1 truncate">
              {{ folder.name }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ (flowsByFolder[folder.id] ?? []).length }}
            </span>
            <button
              type="button"
              :title="$t('FLOWS.FOLDER.EDIT')"
              class="rounded p-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 opacity-0 group-hover:opacity-100"
              @click.stop="openEditFolder(folder)"
            >
              <span class="i-lucide-pencil size-3.5" />
            </button>
            <button
              type="button"
              :title="$t('FLOWS.FOLDER.DELETE')"
              class="rounded p-1 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-1 opacity-0 group-hover:opacity-100"
              @click.stop="confirmDeleteFolder(folder)"
            >
              <span class="i-lucide-trash-2 size-3.5" />
            </button>
          </div>

          <!-- Folder flows -->
          <div
            v-if="!collapsedFolders.has(folder.id)"
            class="flex flex-col gap-1 px-2 pb-2"
          >
            <div
              v-if="(flowsByFolder[folder.id] ?? []).length === 0"
              class="px-3 py-4 text-xs text-n-slate-10 text-center italic"
            >
              {{ $t('FLOWS.FOLDER.NONE') }}
            </div>
            <FlowCard
              v-for="flow in flowsByFolder[folder.id] ?? []"
              :key="flow.id"
              :flow="flow"
              :is-favorite="favoriteIds.includes(flow.id)"
              :account-id="accountId"
              draggable="true"
              :class="draggingFlowId === flow.id ? 'opacity-40' : 'opacity-100'"
              @toggle-favorite="toggleFavorite"
              @delete="confirmDelete"
              @dragstart="onDragStart(flow.id)"
              @dragend="onDragEnd"
            />
          </div>
        </div>

        <!-- Flows sem pasta -->
        <div
          v-if="
            (flowsByFolder[NO_FOLDER] ?? []).length > 0 || folders.length > 0
          "
          class="flex flex-col gap-1 rounded-xl border transition-colors"
          :class="
            dragOverFolderId === NO_FOLDER
              ? 'border-n-brand bg-n-alpha-2 p-2'
              : 'border-transparent'
          "
          @dragover="onDragOver($event, NO_FOLDER)"
          @dragleave="onDragLeave"
          @drop="onDrop($event, NO_FOLDER)"
        >
          <p
            v-if="
              folders.length > 0 && (flowsByFolder[NO_FOLDER] ?? []).length > 0
            "
            class="text-xs font-medium text-n-slate-10 px-1 mb-1"
          >
            {{ $t('FLOWS.FOLDER.NONE') }}
          </p>
          <FlowCard
            v-for="flow in flowsByFolder[NO_FOLDER] ?? []"
            :key="flow.id"
            :flow="flow"
            :is-favorite="favoriteIds.includes(flow.id)"
            :account-id="accountId"
            draggable="true"
            :class="draggingFlowId === flow.id ? 'opacity-40' : 'opacity-100'"
            @toggle-favorite="toggleFavorite"
            @delete="confirmDelete"
            @dragstart="onDragStart(flow.id)"
            @dragend="onDragEnd"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>

  <!-- Create Flow Modal -->
  <div
    v-if="showCreateModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="showCreateModal = false"
  >
    <div
      class="bg-n-background rounded-2xl shadow-xl w-full max-w-md p-6 flex flex-col gap-4"
    >
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('FLOWS.CREATE.TITLE') }}
      </h2>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('FLOWS.CREATE.NAME_LABEL') }}
        </label>
        <input
          v-model="newFlowName"
          type="text"
          :placeholder="$t('FLOWS.CREATE.NAME_PLACEHOLDER')"
          class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
          @keydown.enter="createFlow"
          @keydown.escape="showCreateModal = false"
        />
      </div>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1"
          @click="showCreateModal = false"
        >
          {{ $t('FLOWS.CANCEL') }}
        </button>
        <button
          type="button"
          :disabled="!newFlowName.trim() || isCreating"
          class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed"
          @click="createFlow"
        >
          <span
            v-if="isCreating"
            class="i-lucide-loader-2 size-4 animate-spin mr-1 inline-block"
          />
          {{ $t('FLOWS.CREATE.SUBMIT') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Folder create/edit Modal -->
  <div
    v-if="showFolderModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="showFolderModal = false"
  >
    <div
      class="bg-n-background rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4"
    >
      <h2 class="text-base font-semibold text-n-slate-12">
        {{
          folderModalMode === 'create'
            ? $t('FLOWS.FOLDER.NEW')
            : $t('FLOWS.FOLDER.EDIT')
        }}
      </h2>
      <input
        v-model="folderModalName"
        type="text"
        :placeholder="$t('FLOWS.FOLDER.NAME_PLACEHOLDER')"
        class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        @keydown.enter="saveFolder"
        @keydown.escape="showFolderModal = false"
      />
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1"
          @click="showFolderModal = false"
        >
          {{ $t('FLOWS.CANCEL') }}
        </button>
        <button
          type="button"
          :disabled="!folderModalName.trim() || isSavingFolder"
          class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed"
          @click="saveFolder"
        >
          <span
            v-if="isSavingFolder"
            class="i-lucide-loader-2 size-4 animate-spin mr-1 inline-block"
          />
          {{ $t('FLOWS.SAVE') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Flow Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="showDeleteModal = false"
  >
    <div
      class="bg-n-background rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4"
    >
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('FLOWS.DELETE.CONFIRM_TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{
          $t('FLOWS.DELETE.CONFIRM_MESSAGE', {
            name: flowToDelete?.name ?? '',
          })
        }}
      </p>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1"
          @click="showDeleteModal = false"
        >
          {{ $t('FLOWS.CANCEL') }}
        </button>
        <button
          type="button"
          class="rounded-lg bg-n-ruby-9 px-4 py-2 text-sm font-medium text-white hover:bg-n-ruby-10 transition"
          @click="deleteFlow"
        >
          {{ $t('FLOWS.DELETE.CONFIRM_TITLE') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Folder Modal -->
  <div
    v-if="showFolderDeleteModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="showFolderDeleteModal = false"
  >
    <div
      class="bg-n-background rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4"
    >
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('FLOWS.FOLDER.DELETE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{
          $t('FLOWS.DELETE.CONFIRM_MESSAGE', {
            name: folderToDelete?.name ?? '',
          })
        }}
      </p>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1"
          @click="showFolderDeleteModal = false"
        >
          {{ $t('FLOWS.CANCEL') }}
        </button>
        <button
          type="button"
          class="rounded-lg bg-n-ruby-9 px-4 py-2 text-sm font-medium text-white hover:bg-n-ruby-10 transition"
          @click="deleteFolder"
        >
          {{ $t('FLOWS.FOLDER.DELETE') }}
        </button>
      </div>
    </div>
  </div>
</template>
